import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Shield, Users, Zap, Layers, Radio, Cable, HardHat, Building2, ChevronRight } from "lucide-react";
import { Reveal, MaskedLines, Overline } from "../components/Reveal";
import { SectionHeading, CTABand, ServiceCard, Testimonials, ClientsStrip } from "../components/shared";
import { SERVICES, LIFECYCLE, WHY, SECTORS, SAMPLE_PROJECTS, IMAGES } from "../data/content";

const WHY_ICONS = [HardHat, Users, Shield, Layers, Zap, Building2, Radio, Cable];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#0B1F3A] text-white" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.heroTowers} alt="Telecom tower infrastructure" className="w-full h-full object-cover opacity-45" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1F3A] via-[#0B1F3A]/80 to-[#0B1F3A]/30" />
      <div className="absolute inset-0 tech-grid-dark opacity-40" />
      <div className="grain" />
      <div className="relative w-full max-w-[1360px] mx-auto px-5 md:px-8 pb-20 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Overline dark>Telecom Infrastructure &amp; Engineering</Overline>
        </motion.div>
        <MaskedLines
          className="mt-6 font-head font-extrabold text-3xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.0] max-w-4xl"
          lines={["Building and Maintaining", "India's Connected", "Infrastructure"]}
        />
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 text-lg md:text-xl text-[#CBD5E1] max-w-2xl leading-relaxed">
          Airanet India Pvt Ltd delivers telecom network deployment, infrastructure engineering, operations, maintenance and technical workforce solutions for operators, OEMs, tower companies and enterprise clients.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-4">
          <Link to="/services" data-testid="hero-explore" className="inline-flex items-center gap-2 bg-[#F28C28] text-[#0B1F3A] font-semibold px-7 py-3.5 rounded-md hover:-translate-y-0.5 transition-transform">Explore Our Capabilities <ChevronRight className="h-4 w-4" /></Link>
          <Link to="/contact" data-testid="hero-discuss" className="inline-flex items-center gap-2 border border-white/25 backdrop-blur-sm px-7 py-3.5 rounded-md hover:bg-white/10">Discuss a Project</Link>
        </motion.div>
      </div>
    </section>
  );
};

const Home = () => {
  const trust = ["Telecom Engineering", "Network Rollout", "Operations & Maintenance", "Infrastructure", "Manpower"];

  return (
    <>
      <Hero />

      {/* Trust marquee */}
      <div className="bg-[#0B1F3A] border-t border-white/10 py-4">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {[...trust, ...trust, ...trust].map((t, i) => (
            <span key={i} className="mx-8 font-head font-semibold text-sm uppercase tracking-[0.2em] text-white/50 inline-flex items-center gap-8">
              {t} <span className="h-1.5 w-1.5 rounded-full bg-[#F28C28]" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* Company intro */}
      <section className="py-24 md:py-32 bg-white" data-testid="intro">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <SectionHeading overline="Chapter 01 — Who We Are" title="Your Telecom Infrastructure Execution Partner" />
            <div className="mt-6 space-y-5 text-[#64748B] text-lg leading-relaxed">
              <p>Airanet India Pvt Ltd supports the deployment, operation and maintenance of telecommunications and associated infrastructure across India. We combine technical expertise, field execution capability and disciplined project management to help clients complete network programmes safely, efficiently and to the required quality standards.</p>
              <p>From site surveys and network rollout to installation, commissioning, preventive maintenance and field workforce deployment, our teams provide practical support throughout the infrastructure lifecycle.</p>
            </div>
            <Link to="/about" data-testid="intro-learn" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#1261A0] hover:gap-3 transition-all">Learn More About Airanet <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 h-24 w-24 border-t-2 border-l-2 border-[#F28C28]" />
                <img src={IMAGES.engineer} alt="Network engineer at work" loading="lazy" className="w-full h-[440px] object-cover rounded-lg clip-corner" />
                <div className="absolute -bottom-6 -right-6 bg-white shadow-xl rounded-lg p-5 border border-[#E2E8F0] hidden md:block">
                  <p className="font-head font-extrabold text-2xl text-[#0B1F3A]">Field-first</p>
                  <p className="text-sm text-[#64748B]">execution across India</p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Core services */}
      <section className="py-24 md:py-32 bg-[#F4F6F8]" data-testid="core-services">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading overline="Chapter 02 — Capabilities" title="Core Services" sub="Six delivery capabilities spanning the full telecom infrastructure lifecycle." />
            <Link to="/services" className="inline-flex items-center gap-2 font-semibold text-[#1261A0] hover:gap-3 transition-all">View All Services <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.slice(0, 6).map((s, i) => <ServiceCard key={s.slug} s={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* Lifecycle horizontal scroll */}
      <LifecycleSection />

      {/* Why Airanet */}
      <section className="py-24 md:py-32 bg-white" data-testid="why">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <SectionHeading overline="Chapter 04 — Why Airanet" title="Built for Execution" sub="We understand that telecom infrastructure programmes require speed, discipline, visibility and consistent execution. Airanet works as an extension of the client's project and operations teams." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.t} delay={(i % 4) * 0.06}>
                  <div className="h-full bg-white border border-[#E2E8F0] rounded-lg p-6 hover-lift hover:border-[#1EA7E1] hover:shadow-lg">
                    <span className="grid place-items-center h-11 w-11 rounded-md bg-[#0B1F3A] text-[#1EA7E1]"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 font-head font-semibold text-[#0B1F3A]">{w.t}</h3>
                    <p className="mt-2 text-sm text-[#64748B] leading-relaxed">{w.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-24 md:py-32 bg-[#0B1F3A] text-white relative overflow-hidden" data-testid="sectors">
        <div className="absolute inset-0 tech-grid-dark opacity-40" /><div className="grain" />
        <div className="relative max-w-[1360px] mx-auto px-5 md:px-8">
          <SectionHeading dark overline="Chapter 05 — Sectors" title="Sectors We Support" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SECTORS.map((s, i) => (
              <Reveal key={s.t} delay={(i % 4) * 0.05}>
                <div className="h-full bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 hover:border-[#1EA7E1] transition-colors">
                  <span className="font-mono2 text-xs text-[#1EA7E1]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-head font-semibold text-lg">{s.t}</h3>
                  <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured projects */}
      <section className="py-24 md:py-32 bg-[#F4F6F8]" data-testid="featured-projects">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading overline="Chapter 06 — Experience" title="Featured Projects" sub="Representative engagements. Client names withheld for commercial confidentiality." />
            <Link to="/projects" className="inline-flex items-center gap-2 font-semibold text-[#1261A0] hover:gap-3 transition-all">View Project Experience <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {SAMPLE_PROJECTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="h-full bg-white border border-[#E2E8F0] rounded-lg overflow-hidden hover-lift hover:shadow-xl">
                  <div className="relative h-48 overflow-hidden"><img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover" /><span className="absolute top-3 left-3 bg-[#0B1F3A]/80 text-white text-xs px-2.5 py-1 rounded">{p.category}</span></div>
                  <div className="p-6">
                    <h3 className="font-head font-semibold text-lg text-[#0B1F3A]">{p.title}</h3>
                    <p className="mt-2 text-sm text-[#64748B]">{p.client_type} · {p.location}</p>
                    <p className="mt-3 text-sm text-[#64748B] leading-relaxed">{p.scope}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QHSE */}
      <section className="py-24 md:py-32 bg-white" data-testid="qhse-home">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <img src={IMAGES.ppe} alt="Engineers wearing PPE" loading="lazy" className="w-full h-[420px] object-cover rounded-lg clip-corner" />
              <div className="absolute -bottom-4 -left-4 h-24 w-24 border-b-2 border-l-2 border-[#F28C28]" />
            </div>
          </Reveal>
          <div>
            <SectionHeading overline="Chapter 07 — QHSE" title="Safety and Quality at the Centre of Delivery" sub="We aim to undertake every assignment with proper planning, trained personnel, site discipline, risk awareness and clear accountability. Our operating approach is built around protecting people, infrastructure, client assets and surrounding communities." />
            <Link to="/qhse" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#1261A0] hover:gap-3 transition-all">View Our QHSE Approach <ChevronRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="py-20 bg-[#F4F6F8]" data-testid="careers-home">
        <div className="max-w-[1360px] mx-auto px-5 md:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-2xl">
            <Overline>Careers</Overline>
            <h2 className="mt-3 font-head font-bold text-3xl md:text-4xl text-[#0B1F3A] tracking-tight">Build India&apos;s Connected Future with Airanet</h2>
            <p className="mt-4 text-[#64748B] text-lg">We are interested in connecting with telecom engineers, field technicians, project managers, civil and electrical professionals, fibre technicians and site supervisors.</p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <Link to="/careers" className="inline-flex justify-center items-center gap-2 bg-[#0B1F3A] text-white font-semibold px-7 py-3.5 rounded-md hover:bg-[#1261A0]">View Opportunities</Link>
            <Link to="/careers#apply" className="inline-flex justify-center items-center gap-2 border border-[#0B1F3A]/20 px-7 py-3.5 rounded-md hover:bg-white">Submit Your CV</Link>
          </div>
        </div>
      </section>

      <Testimonials dark />
      <ClientsStrip />
      <CTABand />
    </>
  );
};

const LifecycleSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-72%"]);
  return (
    <section ref={ref} className="relative h-[260vh] bg-[#112644]" data-testid="lifecycle">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="absolute inset-0 tech-grid-dark opacity-30" />
        <div className="relative max-w-[1360px] mx-auto px-5 md:px-8 w-full">
          <SectionHeading dark overline="Chapter 03 — Lifecycle" title="Supporting the Complete Network Infrastructure Lifecycle" sub="Our teams can support individual work packages or provide coordinated end-to-end delivery across multiple locations." />
        </div>
        <motion.div style={{ x }} className="relative mt-14 flex gap-6 pl-5 md:pl-8 w-max">
          {LIFECYCLE.map((step, i) => (
            <div key={step} className="w-[280px] shrink-0 bg-white/5 border border-white/10 rounded-lg p-7 hover:border-[#1EA7E1] transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-mono2 text-[#F28C28] text-sm">{String(i + 1).padStart(2, "0")}</span>
                <span className="h-2 w-2 rounded-full bg-[#1EA7E1]" />
              </div>
              <h3 className="mt-6 font-head font-bold text-2xl text-white">{step}</h3>
              <div className="mt-4 h-px w-full bg-white/10" />
              <p className="mt-4 text-sm text-[#94A3B8]">Stage {i + 1} of the coordinated delivery process.</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Home;
